import React, { useState, useEffect } from 'react';
import { subscribeToUserCollection } from '../utils/userDataHelper';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import './labcss/batches-pages.css';

const BATCH_COLORS = {
  planned: '#9e9e9e',
  in_progress: '#2196f3',
  curing: '#ff9800',
  testing: '#9c27b0',
  ready: '#4caf50',
  sold: '#795548',
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function CalendarPage() {
  const [batches, setBatches] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [view, setView] = useState('month'); // month or timeline

  useEffect(() => {
    const unsubs = [];
    try {
      unsubs.push(subscribeToUserCollection('batches', (data) => {
        // Sort by createdAt descending
        const sorted = [...data].sort((a, b) => {
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        setBatches(sorted);
      }));
      unsubs.push(subscribeToUserCollection('recipes', (data) => {
        setRecipes(data);
      }));
    } catch (error) {
      console.error('Error loading data:', error);
    }
    return () => unsubs.forEach(u => u());
  }, []);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Get days in month
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();

  // Navigate months
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToToday = () => setCurrentDate(new Date());

  // Get events for a specific date
  const getEventsForDate = (day) => {
    const date = new Date(year, month, day);
    const dateStr = date.toISOString().split('T')[0];
    
    const events = [];
    
    batches.forEach(batch => {
      // Made date
      if (batch.madeDate === dateStr) {
        events.push({
          type: 'made',
          batch,
          label: `Made: ${batch.name || batch.recipeName}`,
          color: '#4caf50',
        });
      }
      
      // Ready date (6 weeks after made)
      if (batch.madeDate) {
        const madeDate = new Date(batch.madeDate);
        const readyDate = new Date(madeDate);
        readyDate.setDate(readyDate.getDate() + 42);
        if (readyDate.toISOString().split('T')[0] === dateStr) {
          events.push({
            type: 'ready',
            batch,
            label: `Ready: ${batch.name || batch.recipeName}`,
            color: '#ff9800',
          });
        }
      }
    });
    
    return events;
  };

  // Check if date is today
  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  };

  // Selected date events
  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  // Timeline view - next 8 weeks
  const getTimelineData = () => {
    const weeks = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let w = 0; w < 8; w++) {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() + (w * 7));
      
      const weekBatches = batches.filter(batch => {
        if (!batch.madeDate) return false;
        const madeDate = new Date(batch.madeDate);
        const readyDate = new Date(madeDate);
        readyDate.setDate(readyDate.getDate() + 42);
        
        // Check if this batch is curing during this week
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        
        return madeDate <= weekEnd && readyDate >= weekStart;
      });
      
      weeks.push({
        weekStart,
        batches: weekBatches,
      });
    }
    
    return weeks;
  };

  const timelineData = getTimelineData();

  return (
    <>
      <div className="sl-page calendar-page">
        {/* Page Header */}
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Batches', to: '/lab/batches' },
            { label: 'Calendar' }
          ]} />
          <div className="pageheading">Batch Calendar</div>
          <div className="pagedescription">Visual calendar for batch planning and curing timeline.</div>
        </div>

        <div className="calendar-page-content borderleft">
          {/* View Toggle */}
          <section className="mypadding borderbottom">
            <div className="cal-view-tabs">
              <button
                className={`cal-view-tab${view === 'month' ? ' active' : ''}`}
                onClick={() => setView('month')}
              >
                Month view
              </button>
              <button
                className={`cal-view-tab${view === 'timeline' ? ' active' : ''}`}
                onClick={() => setView('timeline')}
              >
                Timeline view
              </button>
            </div>
          </section>

      {view === 'month' ? (
          <section className="mypadding borderbottom">
            {/* Month Navigation */}
            <div className="cal-nav">
              <button className="cal-nav-btn" onClick={prevMonth}>Previous</button>
              <div className="cal-nav-center">
                <h3 style={{ margin: 0 }}>{MONTHS[month]} {year}</h3>
                <button className="cal-today-btn" onClick={goToToday}>Today</button>
              </div>
              <button className="cal-nav-btn" onClick={nextMonth}>Next →</button>
            </div>

            {/* Calendar Grid */}
            <div className="cal-grid">
              {/* Day headers */}
              {DAYS.map(day => (
                <div key={day} className="cal-day-header">{day}</div>
              ))}

              {/* Empty cells for days before month starts */}
              {Array(firstDayOfMonth).fill(null).map((_, i) => (
                <div key={`empty-${i}`} className="cal-day-empty" />
              ))}

              {/* Days of month */}
              {Array(daysInMonth).fill(null).map((_, i) => {
                const day = i + 1;
                const events = getEventsForDate(day);
                const today = isToday(day);
                const isSelected = selectedDate === day;

                return (
                  <div
                    key={day}
                    className={`cal-day${isSelected ? ' selected' : ''}`}
                    onClick={() => setSelectedDate(isSelected ? null : day)}
                  >
                    <div className={`cal-day-number${today ? ' today' : ''}`}>
                      {day}
                    </div>

                    {/* Event dots */}
                    <div className="cal-events">
                      {events.slice(0, 3).map((event, ei) => (
                        <div key={ei} className="cal-event-dot" style={{ background: event.color }}>
                          {event.batch.name?.substring(0, 15) || 'Batch'}
                        </div>
                      ))}
                      {events.length > 3 && (
                        <div className="cal-event-more">+{events.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Selected Date Details */}
            {selectedDate && (
              <div className="cal-date-detail">
                <h4 style={{ margin: '0 0 12px' }}>
                  {MONTHS[month]} {selectedDate}, {year}
                </h4>
                {selectedDateEvents.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {selectedDateEvents.map((event, i) => (
                      <div key={i} className="cal-event-item" style={{ borderLeftColor: event.color }}>
                        <div style={{ fontWeight: 600 }}>{event.label}</div>
                        <div style={{ fontSize: '0.85em', color: '#64748b', marginTop: 4 }}>
                          {event.batch.bars || '?'} bars • Status: {event.batch.status}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: '#64748b' }}>No events on this date</div>
                )}
              </div>
            )}
          </section>
      ) : (
          /* Timeline View */
          <section className="mypadding">
            <h3 style={{ marginBottom: 16 }}>Next 8 Weeks - Curing Timeline</h3>
            
            {/* Legend */}
            <div className="cal-legend">
              <div className="cal-legend-item">
                <div className="cal-legend-swatch" style={{ background: '#059669' }} />
                <span>Week 1-2 (early cure)</span>
              </div>
              <div className="cal-legend-item">
                <div className="cal-legend-swatch" style={{ background: '#d97706' }} />
                <span>Week 3-4 (mid cure)</span>
              </div>
              <div className="cal-legend-item">
                <div className="cal-legend-swatch" style={{ background: '#2563eb' }} />
                <span>Week 5-6 (almost ready)</span>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <div className="cal-timeline-grid">
                {/* Week headers */}
                {timelineData.map((week, i) => (
                  <div key={i} className="cal-timeline-header">
                    Week {i + 1}
                    <div style={{ fontSize: '0.8em', fontWeight: 400, color: '#64748b' }}>
                      {week.weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                ))}

                {/* Batch rows */}
                {batches.filter(b => b.madeDate && b.status === 'curing').map(batch => {
                  const madeDate = new Date(batch.madeDate);
                  const readyDate = new Date(madeDate);
                  readyDate.setDate(readyDate.getDate() + 42);

                  return timelineData.map((week, wi) => {
                    const weekEnd = new Date(week.weekStart);
                    weekEnd.setDate(weekEnd.getDate() + 6);
                    
                    const isInRange = madeDate <= weekEnd && readyDate >= week.weekStart;
                    if (!isInRange) {
                      return <div key={`${batch.id}-${wi}`} className="cal-timeline-cell" />;
                    }

                    const daysSinceMade = Math.floor((week.weekStart - madeDate) / (1000 * 60 * 60 * 24));
                    const cureWeek = Math.ceil(daysSinceMade / 7) + 1;
                    
                    let color = '#059669';
                    if (cureWeek >= 3) color = '#d97706';
                    if (cureWeek >= 5) color = '#2563eb';

                    return (
                      <div key={`${batch.id}-${wi}`} className="cal-timeline-bar" style={{ background: color }}>
                        {batch.name?.substring(0, 12) || 'Batch'}
                        <div style={{ fontSize: '0.85em', opacity: 0.9 }}>Week {cureWeek}</div>
                      </div>
                    );
                  });
                })}
              </div>
            </div>

            {/* Upcoming Ready Dates */}
            <div style={{ marginTop: 24 }}>
              <h4>Upcoming Ready Dates</h4>
              <div className="cal-upcoming-grid">
                {batches
                  .filter(b => b.madeDate && b.status === 'curing')
                  .map(batch => {
                    const madeDate = new Date(batch.madeDate);
                    const readyDate = new Date(madeDate);
                    readyDate.setDate(readyDate.getDate() + 42);
                    const daysLeft = Math.ceil((readyDate - new Date()) / (1000 * 60 * 60 * 24));
                    
                    if (daysLeft < 0) return null;
                    
                    return (
                      <div key={batch.id} className="cal-upcoming-card">
                        <div style={{ fontWeight: 600 }}>{batch.name || batch.recipeName}</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: '0.9em', color: '#64748b' }}>
                          <span>Ready: {readyDate.toLocaleDateString()}</span>
                          <span style={{ color: daysLeft <= 7 ? '#059669' : '#d97706', fontWeight: 500 }}>
                            {daysLeft} days left
                          </span>
                        </div>
                      </div>
                    );
                  })
                  .filter(Boolean)
                }
              </div>
            </div>
          </section>
      )}
        </div>
      <Footer />
      </div>

    </>
  );
}

export default CalendarPage;
